import { motion } from "framer-motion";
import { CheckCircle2, Clock, FileText, Palette, Shield, Zap } from "lucide-react";
import { useState } from "react";
import { ContactModal } from "@/components/ContactModal";

const LogoDesignServices = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <main className="min-h-screen bg-gradient-to-b from-background to-accent-navy/30">
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            Professional Logo Design Services
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-muted-foreground mb-8"
          >
            Get a custom logo that captures your brand essence. Our strategic logo design process delivers memorable, scalable logos for businesses of all sizes.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Start Your Project
          </motion.button>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Our Logo Design Process
          </motion.h2>
          <div className="space-y-6">
            {[
              { step: 1, title: "Discovery & Research", desc: "We learn about your business, target audience, and brand values to inform our design strategy." },
              { step: 2, title: "Concept Development", desc: "Multiple logo concepts created based on research, exploring different styles and approaches." },
              { step: 3, title: "Refinement & Iteration", desc: "Collaborative feedback sessions to refine the chosen concept to perfection." },
              { step: 4, title: "Final Delivery", desc: "Complete logo package with all file formats and usage guidelines." },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-4 items-start"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-coral/20 flex items-center justify-center text-coral font-bold">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services We Offer */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Logo Design Services We Offer
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Palette, title: "Custom Logo Design", desc: "Unique logos tailored to your brand identity and business goals." },
              { icon: Zap, title: "Logo Redesign", desc: "Modernize your existing logo while maintaining brand recognition." },
              { icon: Shield, title: "Brand Identity Packages", desc: "Complete brand systems including colors, typography, and guidelines." },
              { icon: FileText, title: "Logo Variations", desc: "Multiple versions for different applications and mediums." },
            ].map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <service.icon className="w-8 h-8 text-coral mb-4" />
                <h3 className="font-semibold text-lg mb-2">{service.title}</h3>
                <p className="text-muted-foreground">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Types of Logos */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Types of Logos We Create
          </motion.h2>
          <div className="space-y-4">
            {[
              { type: "Wordmarks", desc: "Text-based logos focusing on typography and brand name." },
              { type: "Emblem Logos", desc: "Traditional badges or crests with text inside a symbol." },
              { type: "Abstract Logos", desc: "Geometric shapes representing brand values without literal imagery." },
              { type: "Mascot Logos", desc: "Character-based logos that create brand personality." },
              { type: "Combination Logos", desc: "Text and symbol combined for versatile brand representation." },
            ].map((logo, index) => (
              <motion.div
                key={logo.type}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-4 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-lg mb-1">{logo.type}</h3>
                <p className="text-muted-foreground">{logo.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Logo Design Pricing & Packages
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: "Basic", price: "$299", features: ["2 logo concepts", "2 revisions", "Basic file formats", "3-day delivery"] },
              { name: "Standard", price: "$499", features: ["4 logo concepts", "4 revisions", "All file formats", "Brand guidelines", "5-day delivery"] },
              { name: "Premium", price: "$799", features: ["6 logo concepts", "Unlimited revisions", "Complete brand system", "Social media kit", "Priority support"] },
            ].map((pkg, index) => (
              <motion.div
                key={pkg.name}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-xl mb-2">{pkg.name}</h3>
                <div className="text-3xl font-bold text-coral mb-4">{pkg.price}</div>
                <ul className="space-y-2">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-coral" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Why Choose Koech Creatives for Logo Design
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Zap, title: "Strategic Approach", desc: "We design with business goals in mind, not just aesthetics." },
              { icon: Shield, title: "Experienced Team", desc: "Years of experience across diverse industries and brand types." },
              { icon: Clock, title: "Collaborative Process", desc: "We work closely with you to ensure your vision is realized." },
              { icon: CheckCircle2, title: "Satisfaction Guarantee", desc: "We refine until you're completely satisfied with the result." },
            ].map((reason, index) => (
              <motion.div
                key={reason.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-4"
              >
                <reason.icon className="w-8 h-8 text-coral flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-lg mb-1">{reason.title}</h3>
                  <p className="text-muted-foreground">{reason.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Logo Design FAQ
          </motion.h2>
          <div className="space-y-4">
            {[
              { q: "How long does logo design take?", a: "Typically 3-5 business days for basic packages, 5-7 days for standard, and 7-10 days for premium packages." },
              { q: "What file formats do you provide?", a: "We provide all essential formats: PNG, JPG, SVG, PDF, and AI/EPS source files for future editing." },
              { q: "How many revisions do I get?", a: "Basic packages include 2 revisions, Standard includes 4, and Premium includes unlimited revisions." },
              { q: "Do I own the final logo?", a: "Yes, once payment is complete, you own full rights to use the logo for any purpose." },
            ].map((faq, index) => (
              <motion.div
                key={faq.q}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-4 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-lg mb-2">{faq.q}</h3>
                <p className="text-muted-foreground">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-4"
          >
            Ready to Create Your Perfect Logo?
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xl text-muted-foreground mb-8"
          >
            Let's discuss your brand and create something amazing together.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Get a Free Consultation
          </motion.button>
        </div>
      </section>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </main>
  );
};

export default LogoDesignServices;
